import { handleError } from '@anchorage/sentry';
import { isNotEmpty } from '@anchorage/utils';
import { reactHookForm, yupResolver as yr, type yup } from '@latitude/form';
import { PortoLogoIcon } from '@latitude/icons';
import { Text } from '@latitude/text';
import { useToast } from '@latitude/toast';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { PORTO_SIGNUP_FORM_PAGES } from '../../constants';
import type { PortoSignupFormPage } from '../../types';
import FormButtons from '../Form/FormButtons/FormButtons';
import FormContent from './FormContent/FormContent';
import FormHeader from './FormHeader/FormHeader';
import {
  COMPLETE_YOUR_PROFILE_FORM_SCHEMA,
  ORGANIZATION_DETAILS_FORM_SCHEMA,
} from './validation';

const SUBMIT_ERROR_MESSAGE = `We couldn't submit your information due to a technical issue on our end. Please try refreshing the page. If the issue keeps happening, contact us at help@porto.com.`;

type ProfileFormValues = yup.InferType<
  typeof COMPLETE_YOUR_PROFILE_FORM_SCHEMA
>;
type OrganizationFormValues = yup.InferType<
  typeof ORGANIZATION_DETAILS_FORM_SCHEMA
>;
type LoginFormValues = ProfileFormValues & OrganizationFormValues;

const { yupResolver } = yr;

type Resolver = reactHookForm.Resolver<LoginFormValues>;

type Props = {
  page: PortoSignupFormPage;
  setPage: (page: PortoSignupFormPage) => void;
  onSuccess: () => void;
  isDisabled?: boolean;
};

function Form({ page, setPage, onSuccess, isDisabled = false }: Props) {
  const { useForm, FormProvider } = reactHookForm;
  const { toast } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const getResolver = useCallback(() => {
    if (page === PORTO_SIGNUP_FORM_PAGES.COMPLETE_YOUR_PROFILE) {
      return yupResolver(
        COMPLETE_YOUR_PROFILE_FORM_SCHEMA,
      ) as unknown as Resolver;
    }
    if (page === PORTO_SIGNUP_FORM_PAGES.ORGANIZATION_DETAILS) {
      return yupResolver(
        ORGANIZATION_DETAILS_FORM_SCHEMA,
      ) as unknown as Resolver;
    }
    return undefined;
  }, [page]);

  const methods = useForm<LoginFormValues>({
    mode: 'onTouched',
    resolver: getResolver(),
    disabled: isDisabled,
    defaultValues: {
      mainUserEmail: '',
      mainUserFirstName: '',
      mainUserLastName: '',
      mainUserCountry: '',
      mainUserCheckbox: false,
      organizationName: '',
      organizationCountry: '',
    },
  });

  useEffect(() => {
    // Reset form state when changing pages but keep inputted values.
    // This is needed because when going back to a previous page, the form state is not being reset to just the previous page's values
    methods.reset(undefined, { keepValues: true });
  }, [page, methods]);

  const {
    mutate: finalizePortoRegistration,
    isPending: isFinalizePortoRegistrationLoading,
  } = useMutation({
    // oxlint-disable-next-line typescript/no-explicit-any -- Pre-existing usage; CEXP-20389
    mutationFn: (params: Record<string, any>) =>
      fetch('/v1/submit', {
        method: 'POST',
        mode: 'cors',
        credentials: 'same-origin',
        body: JSON.stringify(params),
      }),
    onSuccess: onSuccess,
    onError: (error) => {
      toast({
        variant: 'danger',
        title: 'Unable to submit',
        description: SUBMIT_ERROR_MESSAGE,
      });
      handleError(
        new Error(`Error submitting data to /v1/submit - ${error}`, {
          cause: error,
        }),
      );
    },
  });

  const onSubmit = useCallback(
    async (data: LoginFormValues) => {
      if (!executeRecaptcha) {
        handleError(new Error('Execute recaptcha not yet available'));
        toast({
          variant: 'danger',
          title: 'Unable to submit',
          description: SUBMIT_ERROR_MESSAGE,
        });
        return;
      }

      executeRecaptcha('signup').then((token) => {
        finalizePortoRegistration({
          org: {
            name: data.organizationName,
            country: data.organizationCountry,
          },
          users: [
            {
              first_name: data.mainUserFirstName,
              last_name: data.mainUserLastName,
              email: data.mainUserEmail,
              country: data.mainUserCountry,
            },
          ].filter(isNotEmpty),
          token,
        });
      });
    },
    [finalizePortoRegistration, executeRecaptcha, toast],
  );

  return (
    <form className="flex size-full flex-col items-start gap-10 overflow-y-scroll px-4 py-10 sm:p-20">
      <PortoLogoIcon className="block h-10 overflow-visible text-white lg:hidden" />
      <FormHeader page={page} />
      <FormProvider {...methods}>
        <FormContent page={page} />
      </FormProvider>
      <div className="flex w-full flex-1 items-end">
        <FormButtons
          page={page}
          setPage={setPage}
          onSubmit={() => {
            methods.handleSubmit(onSubmit)();
          }}
          isDisabled={!methods?.formState?.isValid}
          isLoading={isFinalizePortoRegistrationLoading}
        />
      </div>
      <Text
        variant="captionMedium"
        className="w-full text-left text-color-ref-text-muted lg:text-center"
      >
        This site is protected by reCAPTCHA and the Google{' '}
        <a
          className="text-color-ref-ui-icon-link"
          href="https://policies.google.com/privacy?hl=en"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          className="text-color-ref-ui-icon-link"
          href="https://policies.google.com/terms?hl=en"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </a>{' '}
        apply.
      </Text>
    </form>
  );
}

export default Form;
